import { getMaxTimestamp, getTS } from "@/services/utils";
import {
  getNotCached,
  getIndicesNeededForeca,
  getIndicesNeeded,
  getLayer,
} from "@/utils/auxiliary";
import { getDataAPI } from "@/services/api";

/**
 * updateRainLayer updates the rain layer
 *
 * @param {object} this_p The pointer to the vue instance.
 * @param {object} leafletMap The pointer to the map
 * @param {number} layerLevel
 * @param {number} zoomLevel The current zoom level
 * @param {number} qid The id of the request in the queue
 * @param {number} timeIdx
 * @param {number} layerGroup
 */
export async function updateRainLayer(
  this_p,
  leafletMap,
  layerLevel,
  zoomLevel,
  timeIdx
) {
  let VERBOSE_OUTPUT = false;

  // update the rain layer
  if (VERBOSE_OUTPUT) {
    console.log(":: timeIdx", timeIdx);
    console.log(":: updateRainLayer: [1]: current layerLevel: ", layerLevel);
    console.log(":: updateRainLayer: [1]: current zoom: ", zoomLevel);
    console.log(":: updateRainLayer: [2]: getTilesForArea");
  }

  /*
    Extra steps possible to implement:
      - Go over cache and remove all tiles which are older than 20 minutes?
       var isoDateString = new Date().toISOString();
      - compare the current UTC time and the time in the variable .. ?
      - 
  
      */

  // Find the latest timestamp in the cache. Then, remove all other tiles which forecastTime is older than the latest timestamp
  let maxTS = getMaxTimestamp(this_p);

  if (VERBOSE_OUTPUT) {
    console.log("Max timestamp is ", maxTS);
    console.log("this_p.dataSource", this_p.dataSource);
  }

  // maxTS <= 0 means no cached tile carries a usable forecastTime (e.g. the
  // backend could not fill ForecastTime and sent the zero time
  // "0001-01-01T00:00:00Z", which parses to a large negative number). In that
  // case there is nothing meaningful to compare against and running the
  // eviction below would delete the whole cache on every call, forcing a full
  // backend reload on each slider move.
  //
  // Foreca sources are excluded from time-based eviction entirely: all foreca
  // regions (eu/na/as/aus/global) share one store, and each region's tiles
  // carry that region's own run time — comparing them against a store-wide
  // max would evict every region except the newest one on every call (F4).
  // Foreca invalidation is identity-driven instead: the backend flags
  // clearFrontendCache on a forecast-identity mismatch (handled in api.js)
  // and the updateData flow clears on real updates.
  const timeEvictionApplies =
    this_p.dataSource !== "foreca" && this_p.dataSource !== "foreca_global";
  if (this_p.dataSource.length !== 0 && maxTS > 0 && timeEvictionApplies) {
    // Compute zL based on dataSource
    const zL =
      this_p.dataSource === "foreca" || this_p.dataSource === "foreca_global"
        ? zoomLevel
        : getLayer(zoomLevel, this_p.ref_zoom[this_p.dataSource]);

    // Map dataSource to the corresponding tiles array
    const tilesMap = {
      nw: this_p.tiles,
      hr_de: this_p.tilesHRDE,
      hr_fr: this_p.tilesHRFR,
      hr_nl: this_p.tilesHRNL,
      hr_bi: this_p.tilesHRUK,
      hr_be: this_p.tilesHRBE,
      hr_sc: this_p.tilesHRSC,
      hr_ip: this_p.tilesHRES,
      opera: this_p.tilesOpera,
      foreca: this_p.tilesForeca,
      foreca_global: this_p.tilesForecaGlobal,
    };

    const tiles = tilesMap[this_p.dataSource];

    // Ensure that tiles array is defined and not empty
    if (!tiles || tiles.length === 0) {
      // Optionally handle this case or return early
      return;
    }

    // Iterate over the time indices
    for (let timeIndex = 0; timeIndex < tiles.length; timeIndex++) {
      const tileAtTimeIndex = tiles[timeIndex];

      // Check if tileAtTimeIndex and the layer zL exist
      if (tileAtTimeIndex && tileAtTimeIndex[zL]) {
        const zoomLevelTiles = tileAtTimeIndex[zL];

        if (zoomLevelTiles) {
          for (let [tileKey, tileValue] of zoomLevelTiles) {
            const ts = getTS(tileValue.forecastTime);
            console.log(">>> compare ", ts, maxTS);

            if (ts < maxTS) {
              console.log("removing ", tileKey, tileValue);
              zoomLevelTiles.delete(tileKey);
            }
          }
        }
      }
    }
  }

  let zoomToPass =
    this_p.dataSource == "foreca" || this_p.dataSource == "foreca_global"
      ? zoomLevel
      : zoomLevel;

  await getTilesForArea(this_p, zoomToPass, leafletMap.getBounds(), timeIdx);
  if (VERBOSE_OUTPUT) console.log(":: updateRainLayer: [3]: addLayers");

  let layerToPass =
    this_p.dataSource == "foreca" || this_p.dataSource == "foreca_global"
      ? zoomLevel
      : layerLevel;
  await this_p.addLayers(layerToPass, timeIdx);

  this_p.timeIdx = timeIdx;
}

export /**
 * Check if cache has all tiles needed for the request. If not, it requests the back end to provide missing tiles
 *
 * @param {object} this_p The pointer to the vue instance.
 * @param {number} zoomLevel The current zoom level
 * @param {object} leafletMap The pointer to the map
 */
  async function getTilesForArea(this_p, zoomLevel, bnds, timeIdx) {
  const VERBOSE_OUTPUT = true;
  if (VERBOSE_OUTPUT)
    console.log(
      "------------------------- :: getTilesForArea: [2.0]: this_p.dataSource",
      this_p.dataSource
    );

  // get indices of the tiles in the mab bounds
  let notCachedForecaX = [];
  let notCachedForecaY = [];
  let idcsForecaXFull = [];
  let idcsForecaYFull = [];
  let notCachedIdcs = [];
  let indcsFullSet = [];

  let layer_lvl =
    this_p.dataSource == "foreca" || this_p.dataSource == "foreca_global"
      ? zoomLevel
      : getLayer(zoomLevel, this_p.ref_zoom[this_p.dataSource]);

  if (this_p.dataSource == "foreca" || this_p.dataSource == "foreca_global") {
    if (VERBOSE_OUTPUT) {
      console.log("zoom: ", zoomLevel, "bnds: ", bnds);
    }
    let idcsForecaLinXYMap = getIndicesNeededForeca(zoomLevel, bnds);
    const idcsForecaLin = Array.from(idcsForecaLinXYMap.keys());
    indcsFullSet = idcsForecaLin;

    let CACHE_TILES = true; // set to false to switch off caching on the frontend side
    if (CACHE_TILES) {
      notCachedIdcs = getNotCached(this_p, idcsForecaLin, layer_lvl, timeIdx);
    } else {
      this_p.clearCache();
      for (let i = 0; i < idcsForecaLin.length; i++) {
        notCachedIdcs.push(idcsForecaLin[i]);
      }
    }

    if (VERBOSE_OUTPUT)
      console.log(
        ":: getTilesForArea: [2.a.1]: getNotCached",
        "input: ",
        idcsForecaLin,
        "; result:",
        notCachedIdcs
      );

    notCachedIdcs.forEach((id) => {
      const tile = idcsForecaLinXYMap.get(id);
      if (tile) {
        notCachedForecaX.push(tile.x);
        notCachedForecaY.push(tile.y);
      }
    });

    idcsForecaLinXYMap.forEach((tile) => {
      if (tile) {
        idcsForecaXFull.push(tile.x);
        idcsForecaYFull.push(tile.y);
      }
    });

    if (VERBOSE_OUTPUT) {
      console.log(
        ":: getTilesForArea: [2.b]: getIndicesNeededForeca: idcs needed:",
        idcsForecaLin
      );
      console.log(
        ":: getTilesForArea: [2.b]: getNotCached: idcs not cached:",
        notCachedIdcs
      );

      console.log("idcsForecaLinXYMap: ", idcsForecaLinXYMap);
    }
  } else {
    let indcs = getIndicesNeeded(this_p, zoomLevel, bnds);
    indcsFullSet = indcs;

    notCachedIdcs = getNotCached(this_p, indcs, layer_lvl, timeIdx);
    if (VERBOSE_OUTPUT) {
      console.log(
        ":: getTilesForArea: [2.b]: getIndicesNeeded: idcs needed:",
        indcs
      );
      console.log(
        ":: getTilesForArea: [2.b]: getNotCached: idcs not cached:",
        notCachedIdcs
      );
    }
  }

  if (notCachedIdcs.length) {
    // set request parameters
    const params = {
      timeidx: timeIdx,
      zoom: zoomLevel,
      indcs: notCachedIdcs,
      indcsX: notCachedForecaX,
      indcsY: notCachedForecaY,
      indcsXFull: idcsForecaXFull,
      indcsYFull: idcsForecaYFull,
      forecastUpdate: this_p.latestDataTimestamp[this_p.dataSource],
      indcsFull: indcsFullSet,
      datasource: this_p.dataSource,
      country: this_p.countryCode,
      forecastTimeInApp: this_p.forecastInApp,
      timestamp: this_p.sliderTimestamp,
      timestampFull: this_p.timestampFull,
      timestampOriginal: this_p.timestampOriginal,
      forecastInApp: this_p.forecastInApp,
    };
    if (VERBOSE_OUTPUT)
      console.log(
        ":: getTilesForArea: [2.c]: getDataAPI:  going to request tiles for zoom",
        zoomLevel,
        "; params: ",
        params
      );
    await getDataAPI(this_p, params, layer_lvl, timeIdx);
    if (VERBOSE_OUTPUT)
      console.log(
        ":: getTilesForArea: [2.d]: getDataAPI:  response has been received"
      );
  } else {
    if (VERBOSE_OUTPUT)
      console.log(
        ":: getTilesForArea: [2.c]: everything is available in cache"
      );
  }
}

export async function removeRainLayer(this_p, leafletMapLayerGroup) {
  const VERBOSE_OUTPUT = false;
  // remove all rain tiles from the map
  if (VERBOSE_OUTPUT) {
    if (this_p.dataSource == "nw") {
      console.log(":: [1a] removeRainLayer: this_p.tiles", this_p.tiles);
    } else if (this_p.dataSource == "opera") {
      console.log(
        ":: [1a] removeRainLayer: this_p.tilesOpera",
        this_p.tilesOpera
      );
    } else if (this_p.dataSource == "hr_de") {
      console.log(
        ":: [1a] removeRainLayer: this_p.tilesHRDE",
        this_p.tilesHRDE
      );
    } else if (this_p.dataSource == "hr_fr") {
      console.log(
        ":: [1a] removeRainLayer: this_p.tilesHRFR",
        this_p.tilesHRFR
      );
    } else if (this_p.dataSource == "hr_nl") {
      console.log(
        ":: [1a] removeRainLayer: this_p.tilesHRNL",
        this_p.tilesHRNL
      );
    } else if (this_p.dataSource == "hr_bi") {
      console.log(
        ":: [1a] removeRainLayer: this_p.tilesHRUK",
        this_p.tilesHRUK
      );
    } else if (this_p.dataSource == "hr_be") {
      console.log(
        ":: [1a] removeRainLayer: this_p.tilesHRBE",
        this_p.tilesHRBE
      );
    } else if (this_p.dataSource == "hr_sc") {
      console.log(
        ":: [1a] removeRainLayer: this_p.tilesHRSC",
        this_p.tilesHRSC
      );
    } else if (this_p.dataSource == "hr_ip") {
      console.log(
        ":: [1a] removeRainLayer: this_p.tilesHRES",
        this_p.tilesHRES
      );
    }
  }

  leafletMapLayerGroup.clearLayers();

  if (VERBOSE_OUTPUT) {
    console.log("layers");
    console.log(this_p.tilesForeca);
    console.log(this_p.tilesForecaGlobal);
    console.log("********** layers were cleared *********** ");
  }
}
